import { Injectable } from '@angular/core';

export const VERSION_KEY = 'app_build_hash';

@Injectable({ providedIn: 'root' })
export class SiteDataCleanerService {

  /**
   * Ejecuta la limpieza completa de todos los datos del sitio en el navegador.
   * Se invoca únicamente cuando el usuario hace clic en "Actualizar ahora".
   */
  async clearAllSiteData(): Promise<void> {
    this.clearLocalStorage();
    this.clearSessionStorage();
    this.clearCookies();
    await this.clearIndexedDB();
    await this.clearCacheStorage();
    console.info('[SiteDataCleaner] Limpieza total de datos del sitio completada.');
  }

  /**
   * Prefijos/claves que se deben preservar en localStorage.
   * Todo lo que NO coincida con estos patrones será eliminado.
   */
  private static readonly KEYS_TO_PRESERVE: string[] = [
    'CognitoIdentityServiceProvider',
    'amplify-',
    VERSION_KEY,
  ];

  /** Determina si una clave debe preservarse */
  private shouldPreserve(key: string): boolean {
    return SiteDataCleanerService.KEYS_TO_PRESERVE.some(
      prefix => key === prefix || key.startsWith(prefix)
    );
  }

  /** Limpia todas las claves de localStorage excepto las preservadas (Cognito, Amplify, versión) */
  private clearLocalStorage(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !this.shouldPreserve(key)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
    console.info('[SiteDataCleaner] localStorage limpiado');
  }

  /** Limpia todo el sessionStorage */
  private clearSessionStorage(): void {
    sessionStorage.clear();
  }

  /** Elimina todas las cookies accesibles desde JavaScript */
  private clearCookies(): void {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const name = cookie.split('=')[0].trim();
      if (name) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${globalThis.location.hostname}`;
      }
    }
  }

  /** Elimina todas las bases de datos IndexedDB */
  private async clearIndexedDB(): Promise<void> {
    if (!globalThis.indexedDB?.databases) {
      console.warn('[SiteDataCleaner] indexedDB.databases() no soportado en este navegador.');
      return;
    }
    try {
      const databases = await globalThis.indexedDB.databases();
      const deletePromises = databases
        .filter((db): db is IDBDatabaseInfo & { name: string } => !!db.name)
        .map(db => new Promise<void>((resolve, reject) => {
          const request = globalThis.indexedDB.deleteDatabase(db.name);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
          request.onblocked = () => {
            console.warn('[SiteDataCleaner] IndexedDB bloqueada, se eliminará en el próximo reload.');
            resolve();
          };
        }));
      await Promise.allSettled(deletePromises);
    } catch (error) {
      console.error('[SiteDataCleaner] Error limpiando IndexedDB:', error);
    }
  }

  /** Elimina todos los caches del Cache Storage (Service Worker caches) */
  private async clearCacheStorage(): Promise<void> {
    if (!('caches' in globalThis)) {
      return;
    }
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    } catch (error) {
      console.error('[SiteDataCleaner] Error limpiando Cache Storage:', error);
    }
  }
}
